<template>
  <div class="student-routine-page">
    <div class="student-routine-toolbar student-routine-screen-only">
      <h5>Class Routine</h5>
      <button v-if="hasRoutine" type="button" class="btn btn-sm btn-info" @click="printRoutine">
        <i class="fas fa-print"></i> Print Routine
      </button>
    </div>

    <div v-if="loading" class="student-routine-state">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Loading class routine...</span>
    </div>

    <div v-else-if="hasRoutine" class="student-routine-print-area">
      <div class="student-routine-heading text-center">
        <h2>CANTONMENT ENGLISH SCHOOL &amp; COLLEGE</h2>
        <h3>WEEKLY CLASS ROUTINE</h3>
        <div class="student-routine-meta">
          <strong v-if="className">CLASS: {{ className }}</strong>
          <strong v-if="sectionName">SECTION: {{ sectionName }}</strong>
          <span>ACADEMIC SCHEDULE</span>
        </div>
      </div>

      <div class="student-routine-table-wrap">
        <table class="table student-routine-table">
          <thead>
            <tr>
              <th class="student-day-column">
                <div class="student-day-heading">DAY</div>
                <div class="student-season-label">SUMMER</div>
                <div v-if="hasWinterTiming" class="student-season-label student-winter-label">WINTER</div>
              </th>
              <th
                v-for="(routine, index) in routineHeaders"
                :key="'period-' + index"
                class="student-period-column"
              >
                <div class="student-period-name">{{ slotLabel(routine, index) }}</div>
                <div class="student-period-time">
                  {{ formatTime(routine.duration && routine.duration.start) }} -
                  {{ formatTime(routine.duration && routine.duration.end) }}
                </div>
                <div v-if="hasWinterTiming" class="student-period-time student-winter-time">
                  {{ formatTime(routine.duration && routine.duration.winter_start) }} -
                  {{ formatTime(routine.duration && routine.duration.winter_end) }}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(routineDay, dayIndex) in routineDetails" :key="'day-' + dayIndex">
              <th class="student-day-cell">{{ routineDay.day }}</th>
              <td
                v-for="(header, periodIndex) in routineHeaders"
                :key="'slot-' + dayIndex + '-' + periodIndex"
                :class="{
                  'student-special-slot-cell': slotType(detailAt(routineDay, periodIndex)) !== 'class'
                }"
              >
                <template v-if="detailAt(routineDay, periodIndex)">
                  <div
                    v-if="slotType(detailAt(routineDay, periodIndex)) !== 'class'"
                    class="student-routine-special"
                    :class="'student-special-' + slotType(detailAt(routineDay, periodIndex))"
                  >
                    <i
                      :class="slotType(detailAt(routineDay, periodIndex)) === 'tiffin'
                        ? 'fas fa-utensils'
                        : 'fas fa-bullhorn'"
                    ></i>
                    <strong>{{ slotLabel(detailAt(routineDay, periodIndex), periodIndex) }}</strong>
                    <span>
                      {{ slotType(detailAt(routineDay, periodIndex)) === 'tiffin'
                        ? 'Recess'
                        : 'Daily Meeting' }}
                    </span>
                  </div>
                  <div
                    v-else-if="isActiveClass(detailAt(routineDay, periodIndex))"
                    class="student-routine-entry"
                  >
                    <strong>{{ detailAt(routineDay, periodIndex).subject || '--' }}</strong>
                    <span>{{ detailAt(routineDay, periodIndex).teacher || '--' }}</span>
                  </div>
                  <div v-else class="student-routine-off">OFF</div>
                </template>
                <div v-else class="student-routine-off">OFF</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else class="student-routine-state student-routine-empty">
      <i class="far fa-calendar-times"></i>
      <span>No Class Routine Found</span>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      routineDetails: [],
      loading: true,
      errors: []
    };
  },
  computed: {
    hasRoutine() {
      return Array.isArray(this.routineDetails) && this.routineDetails.some(
        day => Array.isArray(day.details) && day.details.length
      );
    },
    routineHeaders() {
      if (!Array.isArray(this.routineDetails)) return [];
      const days = this.routineDetails.filter(day => Array.isArray(day.details));
      if (!days.length) return [];
      return days.reduce((longest, day) =>
        day.details.length > longest.length ? day.details : longest, []
      );
    },
    hasWinterTiming() {
      return this.routineHeaders.some(item =>
        item.duration && (item.duration.winter_start || item.duration.winter_end)
      );
    },
    className() {
      return this.$parent && this.$parent.data && this.$parent.data.stdclass
        ? this.$parent.data.stdclass.name
        : '';
    },
    sectionName() {
      return this.$parent && this.$parent.data && this.$parent.data.section
        ? this.$parent.data.section.name
        : '';
    }
  },
  methods: {
    detailAt(day, index) {
      return day && Array.isArray(day.details) ? day.details[index] : null;
    },
    slotType(routine) {
      return routine && routine.duration && routine.duration.slot_type
        ? routine.duration.slot_type
        : 'class';
    },
    slotLabel(routine, index) {
      if (routine && routine.duration && routine.duration.label) {
        return routine.duration.label;
      }
      if (this.slotType(routine) === 'tiffin') return 'Tiffin Break';
      if (this.slotType(routine) === 'form_meeting') return 'Form Meeting';
      return 'Period ' + (index + 1);
    },
    isActiveClass(routine) {
      return routine && (routine.status == 1 || (routine.subject && routine.status == null));
    },
    formatTime(value) {
      return value || '--';
    },
    printRoutine() {
      window.print();
    },
    getRoutine() {
      this.loading = true;
      axios
        .get(
          'class-routine/' +
          this.$route.params.classID +
          '/' +
          this.$route.params.sectionID
        )
        .then(response => {
          this.routineDetails = Array.isArray(response.data) ? response.data : [];
        })
        .catch(error => {
          this.errors = error && error.response ? error.response.data : [];
          this.routineDetails = [];
        })
        .then(() => {
          this.loading = false;
          this.$root.loading = false;
        });
    }
  },
  created() {
    this.getRoutine();
  }
};
</script>

<style>
.student-routine-page { padding-bottom: 16px; }
.student-routine-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; padding: 10px 14px; border-radius: 4px; background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,.08); }
.student-routine-toolbar h5 { margin: 0; color: #343a40; font-weight: 700; text-transform: uppercase; }
.student-routine-print-area { padding: 14px; border-radius: 4px; background: #fff; box-shadow: 0 1px 5px rgba(0,0,0,.1); }
.student-routine-heading h2 { margin: 0; color: #1f2933; font-size: 26px; font-weight: 700; }
.student-routine-heading h3 { margin: 4px 0 10px; font-size: 17px; font-weight: 700; text-decoration: underline; }
.student-routine-meta { display: inline-flex; align-items: center; gap: 18px; margin-bottom: 8px; padding: 8px 18px; border: 1px solid #d5d9dd; border-radius: 4px; background: #f1f2f3; font-size: 14px; }
.student-routine-meta span { color: #6c757d; font-weight: 700; }
.student-routine-table-wrap { width: 100%; overflow-x: auto; }
.student-routine-table { width: 100%; min-width: 720px; table-layout: fixed; border-collapse: separate; border-spacing: 3px; margin: 0; }
.student-routine-table th, .student-routine-table td { vertical-align: middle !important; text-align: center; border: 1px solid #d5d9dd !important; }
.student-routine-table thead th { padding: 7px 4px; color: #fff; background: #0789ad; }
.student-day-column, .student-day-cell { width: 62px; }
.student-day-cell { color: #343a40; font-weight: 700; background: #f8fafb; }
.student-day-heading { margin-bottom: 5px; font-weight: 700; }
.student-season-label { padding: 3px 2px; border-radius: 4px; background: rgba(255,255,255,.22); font-size: 9px; line-height: 15px; }
.student-winter-label { margin-top: 3px; }
.student-period-column { min-width: 105px; }
.student-period-name { margin-bottom: 5px; font-weight: 700; }
.student-period-time { padding: 3px 2px; border-radius: 4px; background: rgba(255,255,255,.22); font-size: 10px; white-space: nowrap; }
.student-winter-time { margin-top: 3px; font-size: 9px; }
.student-routine-table tbody td { height: 66px; padding: 4px; background: #fff; }
.student-routine-entry strong, .student-routine-entry span { display: block; }
.student-routine-entry strong { color: #343a40; font-size: 12px; line-height: 15px; }
.student-routine-entry span { margin-top: 4px; color: #666; font-size: 10px; line-height: 13px; }
.student-routine-off { display: flex; min-height: 54px; align-items: center; justify-content: center; border-radius: 4px; color: #555; background: #eee; font-size: 11px; font-weight: 700; }
.student-routine-special { display: flex; min-height: 54px; flex-direction: column; align-items: center; justify-content: center; gap: 2px; border-radius: 4px; font-size: 10px; }
.student-routine-special i { font-size: 14px; }
.student-routine-special strong, .student-routine-special span { display: block; }
.student-special-form_meeting { color: #8b3434; background: #fbe5e5; }
.student-special-tiffin { color: #376d1f; background: #e5f5dc; }
.student-routine-state { display: flex; min-height: 180px; align-items: center; justify-content: center; gap: 9px; border-radius: 4px; color: #56606a; background: #fff; box-shadow: 0 1px 5px rgba(0,0,0,.08); font-size: 15px; font-weight: 600; }
.student-routine-state i { color: #0789ad; font-size: 20px; }

@media (max-width: 767px) {
  .student-routine-print-area { padding: 8px; }
  .student-routine-heading h2 { font-size: 18px; }
  .student-routine-heading h3 { font-size: 14px; }
  .student-routine-meta { gap: 9px; padding: 6px 10px; font-size: 11px; }
}

@media print {
  @page { size: A4 landscape; margin: 8mm; }
  body * { visibility: hidden !important; }
  .student-routine-print-area, .student-routine-print-area * { visibility: visible !important; }
  .student-routine-print-area { position: absolute; top: 0; left: 0; width: 100%; padding: 0 !important; box-shadow: none; }
  .student-routine-screen-only { display: none !important; }
  .student-routine-table-wrap { overflow: visible !important; }
  .student-routine-table { min-width: 0; border-spacing: 2px; }
  .student-routine-heading h2 { font-size: 21px; }
  .student-routine-heading h3 { font-size: 15px; }
  .student-routine-meta { margin-bottom: 3px; padding: 5px 14px; font-size: 12px; }
  .student-routine-table thead th { padding: 4px 2px; }
  .student-routine-table tbody td { height: 52px; padding: 2px; }
  .student-routine-entry strong { font-size: 9px; line-height: 11px; }
  .student-routine-entry span, .student-routine-off { font-size: 8px; line-height: 10px; }
  .student-period-name { font-size: 9px; }
  .student-day-heading { font-size: 9px; }
  .student-season-label { font-size: 6px; line-height: 10px; }
  .student-period-time { font-size: 7px; }
  .student-winter-time { font-size: 6px; }
}
</style>
